import { useChildMatches } from '@tanstack/react-router';
import {
    GuideLinkButton,
    ModalWrapper,
    NewModalBodyWrapper,
    PageHeader,
    useTabs,
    RoutePaths,
} from '@mews-commander/ui';
import { FeatureFlag, ArticlePath, createGuideLink } from '@mews-commander/enums';
import { compactArray } from '@mews/fp';
import { lt, useFeatureFlag } from '@mews/framework';
import { WithChildren } from '@optimus-web/common';
import { useGetAccountingConfigurationOptionsSuspense } from '../../../payment/queries';

const BETA_GUIDE_BASE_URL = 'https://mews.atlassian.net/wiki/external';
const BASE_ROUTE = '/$currentEnterpriseId/Finance/Ledgers';

const GUIDE_LINKS = {
    [`${BASE_ROUTE}/Deposit`]: `${BETA_GUIDE_BASE_URL}/MmQ3ZTgwYzg4NmUxNDhkYmE3ZGYyYjU4MmE2OWVmYTQ`,
    [`${BASE_ROUTE}/Guest`]: `${BETA_GUIDE_BASE_URL}/NzQwOTgyMjlhZDBmNDIyNGIzZDg3OWUwZjY1ZThlNmI`,
    [`${BASE_ROUTE}/City`]: createGuideLink(ArticlePath.CityLedger),
    [`${BASE_ROUTE}/TrialBalance`]: `${BETA_GUIDE_BASE_URL}/MjJjM2UxNGZlODYwNDM3Mzg2M2NiZTU4ZmFiNGQ4ZGI`,
} satisfies Partial<Record<RoutePaths, string>>;

export const FinanceLedgersLayout: React.FC<WithChildren> = ({ children }) => {
    const { data: accountingConfigurationOptions } = useGetAccountingConfigurationOptionsSuspense();
    const canLinkInvoicesConfig = Boolean(accountingConfigurationOptions?.receivableTrackingEnabled);

    const goodDadaLedgersEnabled = useFeatureFlag(FeatureFlag.newFinanceLedgersEnabled);
    const depositLedgerTabEnabled = useFeatureFlag(FeatureFlag.newDepositLedger) || goodDadaLedgersEnabled;
    const guestLedgerTabEnabled = useFeatureFlag(FeatureFlag.mewsGuestLedger) || goodDadaLedgersEnabled;
    const cityLedgerTabEnabled = canLinkInvoicesConfig;
    const trialBalanceEnabled = useFeatureFlag(FeatureFlag.trialBalanceEnabled);

    const ledgerTabs = useTabs(
        BASE_ROUTE,
        compactArray([
            depositLedgerTabEnabled ? 'Deposit' : null,
            guestLedgerTabEnabled ? 'Guest' : null,
            cityLedgerTabEnabled ? 'City' : null,
            trialBalanceEnabled ? 'TrialBalance' : null,
        ])
    );

    const childRoute = useChildMatches({ select: (m) => m.at(-1)?.fullPath });
    const guideLink =
        childRoute && childRoute in GUIDE_LINKS ? GUIDE_LINKS[childRoute as keyof typeof GUIDE_LINKS] : undefined;
    const actions = guideLink ? <GuideLinkButton guideLink={guideLink} /> : undefined;
    const breadcrumbs = [{ title: lt('Ledgers'), path: '' }];

    return (
        <ModalWrapper>
            <PageHeader tabs={ledgerTabs} breadcrumbs={breadcrumbs} actions={actions} />
            <NewModalBodyWrapper>{children}</NewModalBodyWrapper>
        </ModalWrapper>
    );
};
